// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Mutation hooks for work order writes (create, status change).
 *
 * Deliberately NOT optimistic, unlike useAlarmMutation. Alarms live in a
 * database we control, so predicting the result of a patch is safe. Work orders
 * live in a third-party WOMS that exposes no concurrency token: a write is
 * last-write-wins, the stored result can differ from what was sent (status
 * values are mapped, and a concurrent change upstream is silently overwritten),
 * and the response is the only authoritative record. Painting a predicted state
 * would mean showing an operator something the provider's system never agreed
 * to. Both hooks invalidate on success and let the refetch tell the truth.
 */

import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createWorkOrder, patchWorkOrder, type WorkOrderSite } from '../services/workOrderService';
import type { WorkOrder, WorkOrderCreateRequest, WorkOrderStatus } from '../types';

/** Every work order list/summary query, whatever its filters. */
function invalidateWorkOrders(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['work-orders'] });
  qc.invalidateQueries({ queryKey: ['work-order-summary'] });
}

export interface CreateWorkOrderOptions {
  /**
   * Runs once the WOMS has stored the order. Set here, on the hook, rather
   * than on `mutate()`: react-query drops `mutate()` callbacks when the
   * calling component has unmounted, but keeps the hook's. A slow create
   * that outlives its modal (closed, or navigated away from) must still run
   * whatever the caller chains after it — an alarm link, say — or the order
   * exists upstream with nothing in Helios pointing at it.
   */
  onSuccess?: (_workOrder: WorkOrder, _request: WorkOrderCreateRequest) => void;
}

/**
 * `siteId` is the site the order is created at; null (whole fleet) uses the deprecated unscoped route.
 *
 * `options` is read fresh on every render (the caller's continuation closes
 * over its own current props), so it cannot be read directly from `onSuccess`
 * — by the time a slow create settles, a reused, unkeyed caller may have
 * re-rendered for a different alarm and replaced it. `onMutate` runs
 * synchronously inside `mutate()`, before any later render can land, so
 * snapshotting `optionsRef.current` there freezes the continuation that was
 * current when this request actually started.
 */
export function useCreateWorkOrder(siteId: WorkOrderSite, options: CreateWorkOrderOptions = {}) {
  const qc = useQueryClient();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  return useMutation<WorkOrder, Error, WorkOrderCreateRequest, CreateWorkOrderOptions>({
    mutationFn: request => createWorkOrder(siteId, request),
    onMutate: () => optionsRef.current,
    onSuccess: (workOrder, request, context) => {
      invalidateWorkOrders(qc);
      context?.onSuccess?.(workOrder, request);
    },
  });
}

export interface WorkOrderStatusVars {
  /** The order's site; null (whole fleet) uses the deprecated unscoped route. */
  siteId: WorkOrderSite;
  id: string;
  status: WorkOrderStatus;
}

export function useWorkOrderStatus() {
  const qc = useQueryClient();
  return useMutation<WorkOrder, Error, WorkOrderStatusVars>({
    mutationFn: ({ siteId, id, status }) => patchWorkOrder(siteId, id, { status }),
    onSuccess: (order, { siteId, id }) => {
      // The response is the authoritative record: seed the lookup from it so a
      // reader sees the stored status at once, then let the refetch confirm.
      qc.setQueryData(['work-orders', 'by-id', siteId, id], order);
      invalidateWorkOrders(qc);
    },
  });
}

export interface WorkOrderAssignVars {
  siteId: WorkOrderSite;
  id: string;
  /** A business owner id from `listBusinessOwners`; empty string unassigns nothing (upstream has no clear). */
  business_owner: string;
}

/** Reassign an order's business owner; the WOMS is the record, as with status. */
export function useWorkOrderAssign() {
  const qc = useQueryClient();
  return useMutation<WorkOrder, Error, WorkOrderAssignVars>({
    mutationFn: ({ siteId, id, business_owner }) => patchWorkOrder(siteId, id, { business_owner }),
    onSuccess: (order, { siteId, id }) => {
      qc.setQueryData(['work-orders', 'by-id', siteId, id], order);
      invalidateWorkOrders(qc);
    },
  });
}
